"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, HardHat, Sun, House as Home, Building2, CircleCheck as CheckCircle2, ArrowRight, Award } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const audiences = [{
  icon: Home,
  title: "Roofing contractors",
  description: "Full perimeter roof-edge handrails and toe-boards configured for strip-and-re-tile, flat roof replacements, and fascia works without obstructing trade access."
}, {
  icon: Sun,
  title: "Solar installers",
  description: "Rapid, compliant edge guarding and access towers suited to commercial and residential PV installations, solar farm substations, and refits."
}, {
  icon: Building2,
  title: "Main contractors & developers",
  description: "Temporary works compliant edge protection systems engineered for slab edges, steel framing, stairwells, and multi-storey builds across the South East."
}];
const useCases = [{
  title: "Roof-edge fall prevention",
  detail: "Double guardrails, intermediate mesh infills, and secure toe-boards providing zero-gap edge containment on pitched or flat structures."
}, {
  title: "Temporary works compliance",
  detail: "Calculated wind loadings, TG20 compliant layouts, and documented sign-offs ready for Principal Contractor handovers and HSE inspection."
}, {
  title: "Non-penetrative free-standing systems",
  detail: "Counterweighted, freestanding options that protect delicate membrane roofs and pre-finished surfaces from anchor punctures."
}, {
  title: "Fast adaptation & struck-down service",
  detail: "Flexible alterations as build stages progress, managed directly by reliable certified scaffolders to keep trades on schedule."
}];
const accreditations = [{
  name: "NASC",
  role: "Full trade standards & audit compliance"
}, {
  name: "CHAS",
  role: "Accredited health & safety evaluation"
}];
export default function Edgeprotection() {
  const prefersReducedMotion = useReducedMotion();
  const fadeIn = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="edgeprotectionoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block / Answer-First Opening */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <Badge variant="outline" className="mb-4 px-3 py-1 text-xs font-semibold text-foreground border-border bg-card">
            Temporary works & fall prevention
          </Badge>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-6 font-default">
            Edge protection
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-6 font-default">
            Safe, site-specific roof-edge guarding and temporary fall prevention systems for commercial, residential, roofing, and solar projects across Berkshire, Surrey, Hampshire, Buckinghamshire, London, and the South East.
          </p>

          <div className="flex flex-wrap items-center gap-4 pt-2">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestQuotePage">
                Request a site assessment
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-border hover:bg-accent">
              <Link to="/HealthSafetyAccreditationsPage">
                View safety standards
              </Link>
            </Button>
          </div>
        </div>

        {/* Audience Grid */}
        <div className="mb-16">
          <div className="mb-8">
            <h2 className="text-2xl font-bold tracking-tight text-foreground font-default">
              Engineered for trade specialists and site managers
            </h2>
            <p className="text-muted-foreground mt-2 text-base font-default">
              Practical, compliant access solutions configured around your specific build sequence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {audiences.map((item, i) => {
            const IconComp = item.icon;
            return <motion.div key={item.title} data-index={i} initial={fadeIn.hidden} whileInView={fadeIn.show} viewport={{
              once: true
            }}>
                  <Card className="h-full bg-card border-border shadow-sm flex flex-col justify-between hover:shadow-md transition-[border-color,box-shadow] duration-200">
                    <CardHeader>
                      <div className="size-11 rounded-lg bg-accent text-primary flex items-center justify-center mb-4">
                        <IconComp className="size-6" />
                      </div>
                      <CardTitle className="text-xl font-semibold text-card-foreground font-default">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground text-sm leading-relaxed font-default">
                        {item.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </div>
        </div>

        {/* What BMF Provides / Practical Use Cases */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          <div className="lg:col-span-5 space-y-6">
            <div className="inline-flex items-center gap-2 text-primary font-semibold text-sm">
              <HardHat className="size-5 text-primary" />
              <span>Safety-led installation</span>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
              Edge protection planned around the site, not dropped off as a kit
            </h2>
            <p className="text-muted-foreground leading-relaxed text-base font-default">
              BMF Scaffolding installs reliable edge protection designed to prevent falls from height while keeping working lines clear for trades. Every configuration is inspected, tagged, and handed over with complete temporary works documentation.
            </p>
            <p className="text-muted-foreground leading-relaxed text-base font-default">
              Whether you need perimeter handrails on a pitched roof refit, free-standing roof barriers on a commercial flat deck, or slab-edge protection during concrete frames, our experienced crews install quickly and work in full alignment with your timeline.
            </p>
            
            <div className="pt-2">
              <Link to="/RoofingScaffoldingPage" className="inline-flex items-center text-sm font-semibold text-primary hover:underline group">
                Explore roofing scaffolding services
                <ArrowRight className="size-4 ml-1.5 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {useCases.map((uc, i) => <div key={uc.title} data-index={i} className="bg-card border border-border rounded-lg p-5 shadow-xs flex flex-col justify-start">
                  <div className="flex items-center gap-2.5 mb-2.5 text-foreground font-semibold font-default">
                    <CheckCircle2 className="size-5 text-primary shrink-0" />
                    <span className="text-base">{uc.title}</span>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed font-default">
                    {uc.detail}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* Accreditations & Safety Credentials Strip */}
        <div className="rounded-xl bg-card border border-border p-6 sm:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-2 text-primary font-semibold text-sm">
                <ShieldCheck className="size-5 text-primary" />
                <span>Verified safety accreditations</span>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-foreground font-default">
                Full compliance with NASC and CHAS
              </h3>
              <p className="text-muted-foreground text-sm mt-2 leading-relaxed font-default">
                All edge protection installations follow rigorous NASC safety guidelines and TG20 criteria, supported by Pete Wood&apos;s 20+ years of scaffolding experience and a safety-first team culture.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full lg:w-auto shrink-0">
              {accreditations.map((acc, i) => <div key={acc.name} data-index={i} className="bg-muted/40 border border-border/80 rounded-md p-3.5 text-center flex flex-col items-center justify-center min-w-[140px]">
                  <Award className="size-5 text-primary mb-1" />
                  <span className="font-bold text-foreground text-sm font-default">{acc.name}</span>
                  <span className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{acc.role}</span>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}